import { Link } from '@/components/ui/link';
import { env } from '@/config/env';
import { useLanguage } from '@/i18n/language-context';
import { useAnalytics } from '@/lib/use-analytics';

type StorePlatform = 'ios' | 'android';

function deviceStore(): StorePlatform | null {
  if (typeof navigator === 'undefined') return null;
  const { userAgent, platform, maxTouchPoints } = navigator;
  if (/Android/i.test(userAgent)) return 'android';
  // iPadOS can request desktop pages and identify itself as a Mac.
  if (/iPhone|iPad|iPod/i.test(userAgent) || (/^Mac/i.test(platform) && maxTouchPoints > 1))
    return 'ios';
  // Device-specific placements require a recognized mobile platform.
  return null;
}

export function AppStoreBadges({
  placement = 'footer',
}: {
  placement?: 'footer' | 'home' | 'app_fallback';
}): JSX.Element | null {
  const { t } = useLanguage();
  const track = useAnalytics();
  const preferredStore = deviceStore();
  if (placement !== 'footer' && preferredStore === null) return null;
  const stores = [
    {
      platform: 'ios' as const,
      name: 'App Store',
      label: t.downloadIos,
      href: env.iosAppStoreUrl,
      image: '/brand/stores/app-store-en.svg',
      width: 120,
      height: 40,
    },
    {
      platform: 'android' as const,
      name: 'Google Play',
      label: t.downloadAndroid,
      href: env.androidAppStoreUrl,
      image: '/brand/stores/google-play-en.png',
      width: 135,
      height: 40,
    },
  ].filter((store) => placement === 'footer' || store.platform === preferredStore);

  return (
    <div className="app-store-badges" role="group" aria-label={t.mobileApp}>
      {stores.map((store) => {
        const image = (
          <img
            src={store.image}
            alt={store.name}
            width={store.width}
            height={store.height}
            loading={placement === 'home' ? 'eager' : 'lazy'}
            decoding="async"
          />
        );
        return (
          <div
            className={`app-store-badges__item app-store-badges__item--${store.platform}`}
            key={store.platform}
          >
            {store.href !== null ? (
              <Link
                appearance="bare"
                className="app-store-badges__link"
                href={store.href}
                rel="noreferrer"
                aria-label={store.label}
                onClick={() =>
                  track('Public App Download Clicked', {
                    platform: store.platform,
                    placement,
                  })
                }
              >
                {image}
              </Link>
            ) : (
              <span className="app-store-badges__pending">
                {image}
                <small>{t.storeComingSoon}</small>
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
